import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { usePerioExam } from '@/hooks/usePerioExams';
import type { PerioMeasurement, PerioSite } from '@/utils/perio';
import { PERIO_SITES } from '@/utils/perio';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerioComparisonViewProps {
  examIdA: string;
  examIdB: string;
  labelA: string;
  labelB: string;
  onClose: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildMeasurementMap(
  measurements: PerioMeasurement[],
): Map<string, PerioMeasurement> {
  const map = new Map<string, PerioMeasurement>();
  for (const m of measurements) {
    map.set(`${m.tooth_fdi}:${m.site}`, m);
  }
  return map;
}

type DiffResult = 'improved' | 'worsened' | 'unchanged' | 'no-data';

function diffPD(
  older: PerioMeasurement | undefined,
  newer: PerioMeasurement | undefined,
): DiffResult {
  if (!older || !newer) return 'no-data';
  const oPD = older.pocket_depth_mm ?? 0;
  const nPD = newer.pocket_depth_mm ?? 0;
  if (nPD < oPD) return 'improved';
  if (nPD > oPD) return 'worsened';
  return 'unchanged';
}

function diffColor(diff: DiffResult): string {
  switch (diff) {
    case 'improved': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'worsened': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'unchanged': return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    default: return 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500';
  }
}

// Unique FDI teeth across both exams
function collectTeeth(
  a: PerioMeasurement[],
  b: PerioMeasurement[],
): string[] {
  const set = new Set<string>();
  for (const m of [...a, ...b]) set.add(m.tooth_fdi);
  return Array.from(set).sort();
}

// ── Sub-component: comparison column ─────────────────────────────────────────

function ComparisonCell({
  value,
  diff,
}: {
  value: number | null | undefined;
  diff: DiffResult;
}) {
  return (
    <td className={`border border-gray-200 px-1 py-0.5 text-center text-xs font-medium dark:border-gray-700 ${diffColor(diff)}`}>
      {value ?? '–'}
    </td>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioComparisonView({
  examIdA,
  examIdB,
  labelA,
  labelB,
  onClose,
}: PerioComparisonViewProps) {
  const { t } = useTranslation();
  const { data: measurementsA = [], isLoading: loadA } = usePerioExam(examIdA);
  const { data: measurementsB = [], isLoading: loadB } = usePerioExam(examIdB);

  const mapA = buildMeasurementMap(measurementsA);
  const mapB = buildMeasurementMap(measurementsB);

  const teeth = collectTeeth(measurementsA, measurementsB);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('dental.perio.compare.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 border-b border-gray-200 px-6 py-2 dark:border-gray-700">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-green-200" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{t('dental.perio.compare.improved')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-red-200" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{t('dental.perio.compare.worsened')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-gray-200" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{t('dental.perio.compare.unchanged')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {(loadA || loadB) && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Indlæser...</p>
          )}

          {!loadA && !loadB && (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border border-gray-200 px-2 py-1 text-left dark:border-gray-700">Tand</th>
                  <th className="border border-gray-200 px-2 py-1 text-left dark:border-gray-700">Site</th>
                  {PERIO_SITES.map(site => (
                    <th
                      key={`a-${site}`}
                      className="border border-gray-200 px-2 py-1 text-center dark:border-gray-700"
                    >
                      {labelA}<br />{site}
                    </th>
                  ))}
                  {PERIO_SITES.map(site => (
                    <th
                      key={`b-${site}`}
                      className="border border-gray-200 px-2 py-1 text-center dark:border-gray-700"
                    >
                      {labelB}<br />{site}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teeth.map(fdi =>
                  PERIO_SITES.map(site => {
                    const key = `${fdi}:${site}` as string;
                    const mA = mapA.get(key);
                    const mB = mapB.get(key);
                    const diff = diffPD(mA, mB);

                    return (
                      <tr key={key}>
                        {site === 'MB' && (
                          <td
                            rowSpan={6}
                            className="border border-gray-200 px-2 py-1 text-center font-bold text-gray-700 dark:border-gray-700 dark:text-gray-300"
                          >
                            {fdi}
                          </td>
                        )}
                        <td className="border border-gray-200 px-2 py-1 text-gray-600 dark:border-gray-700 dark:text-gray-400">
                          {site}
                        </td>
                        <ComparisonCell value={mA?.pocket_depth_mm} diff={diff} />
                        <ComparisonCell value={mB?.pocket_depth_mm} diff={diff} />
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Suppress TS warning for unused site variable in map
void (PERIO_SITES as PerioSite[]);
